import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import Magnetic from '../fx/Magnetic';

const VARIANTS = {
  primary:
    'sheen bg-brand-500 text-white shadow-glow hover:bg-brand-400 hover:shadow-glow-aurora active:bg-brand-600 disabled:bg-brand-500/50',
  secondary:
    'glass text-content hover:bg-white/[0.10] active:bg-white/[0.04] border border-white/20 hover:border-white/30',
  ghost: 'bg-transparent text-content-muted hover:text-content hover:bg-white/[0.05]',
  danger: 'sheen bg-danger/90 text-white hover:bg-danger active:bg-danger/80',
  outline:
    'bg-transparent border border-border-strong text-content hover:border-brand-400 hover:text-white',
};

const SIZES = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-[3.25rem] px-7 text-base gap-2.5',
  icon: 'h-10 w-10 p-0 justify-center',
};

/**
 * Spring-animated button. `magnetic` wraps it so it leans toward the cursor.
 * Primary/danger variants get an animated sheen sweep on hover.
 */
const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    className,
    loading = false,
    disabled = false,
    icon: Icon,
    iconRight: IconRight,
    magnetic = false,
    type = 'button',
    ...props
  },
  ref
) {
  const btn = (
    <motion.button
      ref={ref}
      type={type}
      whileTap={{ scale: disabled || loading ? 1 : 0.96 }}
      whileHover={{ y: disabled || loading ? 0 : -1 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      disabled={disabled || loading}
      className={cn(
        'relative inline-flex items-center justify-center rounded-lg font-medium',
        'transition-[color,background-color,border-color,box-shadow] duration-300 outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
        'select-none disabled:cursor-not-allowed disabled:opacity-70',
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {!loading && Icon && <Icon className="h-4 w-4 shrink-0" />}
      {children}
      {!loading && IconRight && <IconRight className="h-4 w-4 shrink-0" />}
    </motion.button>
  );

  if (magnetic && !disabled && !loading) {
    return <Magnetic strength={0.5}>{btn}</Magnetic>;
  }
  return btn;
});

export default Button;
